import { useEffect, useState } from 'react';
import { HelpOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TripLegResponseItem } from '../../../data/models/trip-leg.model';
import { submitDiscussionAsync } from '../../../data/trip.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  selectChargeTypes,
  selectTimeZones,
} from '../../../data/reference.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { attestationUtils } from '../../../services/attestationUtils';
import DateUtils from '../../../services/DateUtils';
import * as accountingSlice from '../../../data/accounting.slice';
import { fetchChargeTypesAsync } from '../../../data/reference.slice';
import { PricingContent } from './PricingContent';
import { TripLegStatusBC } from '../../../data/models/trip-leg-status.model';
import { checkTimestampsFilled } from '../../../utils/timestampValidation';
const PREFIX = 'DiscussionDialog';

const classes = {
  details: `${PREFIX}-details`,
  label: `${PREFIX}-label`,
  priceBreakdown: `${PREFIX}-priceBreakdown`,
  priceItem: `${PREFIX}-priceItem`,
  priceItemLabel: `${PREFIX}-priceItemLabel`,
  priceItemValue: `${PREFIX}-priceItemValue`,
  priceTotal: `${PREFIX}-priceTotal`,
  reason: `${PREFIX}-reason`,
  reasonHeader: `${PREFIX}-reasonHeader`,
  reasonTextBox: `${PREFIX}-reasonTextBox`,
};

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 444,
    padding: 40,
  },
  '& .MuiDialogTitle-root': {
    fontFamily: 'Bitter',
    fontSize: 30,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '40px',
    letterSpacing: '0.25px',
    padding: '0',
    paddingBottom: 24,
  },
  '& .MuiDialogContent-root': {
    padding: '0',
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'visible',

    [`& .MuiTypography-root`]: {
      fontFamily: 'Source Sans Pro',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },

    [`& .${classes.details}`]: {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',

      [`& > .MuiBox-root`]: {
        flex: 1,
        paddingBottom: 8,
      },
    },

    [`& .${classes.label}`]: {
      fontWeight: 600,
    },

    [`& .${classes.priceBreakdown}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },

    [`& .${classes.priceItem}`]: {
      display: 'flex',
      flexDirection: 'row',
    },

    [`& .${classes.priceItemLabel}`]: {
      width: 164,
    },

    [`& .${classes.priceItemValue}`]: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },

    [`& .${classes.priceTotal}`]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '143%',
      letterSpacing: '0.17px',

      [`& .MuiTypography-root`]: {
        fontSize: 16,
      },
    },

    [`& .MuiPaper-root`]: {
      padding: 12,
      height: 200,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',

      [`& .MuiTypography-root`]: {
        fontSize: 16,
      },

      [`& .${classes.reason}`]: {
        color: 'rgba(1, 16, 18, 0.60)',
        fontFamily: 'Source Sans Pro',
        gap: 4,
      },

      [`& .${classes.reasonHeader}`]: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      },

      [`& .${classes.reasonTextBox}`]: {
        border: 'none',
        '& .MuiInputBase-root': {
          background: 'white !important',
          fontSize: 14,
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '143%',
          letterSpacing: '0.17px',
        },
      },
    },
  },
  '& .MuiDialogActions-root': {
    padding: '0',
    paddingTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 24,

    '& .MuiButton-root': {
      width: '100%',
      height: 48,
      fontFamily: 'Source Sans Pro',
      fontSize: 15,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.46px',
    },
  },
});

export default function DiscussionDialog({
  open,
  onClose,
  tripLeg,
  providerId,
  page,
  pageSize,
}: {
  open: boolean;
  onClose: () => void;
  tripLeg: TripLegResponseItem | null;
  providerId: number;
  page: number;
  pageSize: number;
}) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState('');

  const timezones = useAppSelector(selectTimeZones);

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const tripLegReferenceID = tripLeg?.tripLegReferenceID;

  const pricings = useAppSelector(accountingSlice.selectTripLegPricings);
  const chargeTypes = useAppSelector(selectChargeTypes);

  const hasRequiredTimestamps = tripLeg
    ? checkTimestampsFilled(tripLeg)
    : false;

  useEffect(() => {
    if (!tripLeg || !tripLegReferenceID || !hasRequiredTimestamps) {
      return;
    }

    if (!chargeTypes?.length) {
      dispatch(fetchChargeTypesAsync());
    }
    dispatch(
      accountingSlice.fetchTripLegPricingsAsync(
        tripLeg?.passengerUUID ?? '',
        tripLegReferenceID,
      ),
    );
  }, [chargeTypes, tripLegReferenceID, hasRequiredTimestamps]);

  if (!tripLeg) {
    return null;
  }

  const timezone = DateUtils.getTimeZoneIana(
    timezones,
    tripLeg?.pickUpAddressTimeZoneID ?? 0,
  );

  const data = {
    tripID: tripLeg?.tripLegReferenceID,
    pickup: {
      dateTime: attestationUtils.formatDate(
        tripLeg?.pickUpDueTimeUTC ?? '',
        timezone,
        false,
      ),
      location: {
        address: [tripLeg?.pickUpAddressLine1, tripLeg?.pickUpAddressLine2]
          .filter(Boolean)
          .join(', '),
        city: tripLeg?.pickUpCity,
        state: tripLeg?.pickUpStateCode,
        zipCode: tripLeg?.pickUpZipCode,
      },
    },
    dropOff: {
      dateTime: attestationUtils.formatDate(
        tripLeg?.tripLegStatusLogs?.find(
          (tripLegStatusLog) =>
            tripLegStatusLog?.tripLegStatusBC === 'COMPLETED',
        )?.createdOnUTC ?? '',
        timezone,
        false,
      ),
      location: {
        address: [tripLeg?.dropOffAddressLine1, tripLeg?.dropOffAddressLine2]
          .filter(Boolean)
          .join(', '),
        city: tripLeg?.dropOffCity,
        state: tripLeg?.dropOffStateCode,
        zipCode: tripLeg?.dropOffZipCode,
      },
    },
  };

  const submitDiscussion = async () => {
    try {
      dispatch(submitDiscussionAsync(tripLeg.tripLegID, reason, providerId));
      handleClose();
    } catch (error) {
      console.error('Failed to mark tripLeg under discussion', error);
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {t('needsAttention.discuss')} - {data.tripID}
      </DialogTitle>
      <DialogContent>
        <Box className={classes.details}>
          <Box>
            <Typography className={classes.label}>
              {t('needsAttention.pickup')}
            </Typography>
            <Typography>{data.pickup.dateTime}</Typography>
            <Typography>{data.pickup.location.address},</Typography>
            <Typography>
              {data.pickup.location.city}, {data.pickup.location.state},{' '}
              {data.pickup.location.zipCode}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.label}>
              {t('needsAttention.dropOff')}
            </Typography>
            <Typography>{data.dropOff.dateTime}</Typography>
            <Typography>{data.dropOff.location.address}</Typography>
            <Typography>
              {data.dropOff.location.city}, {data.dropOff.location.state},{' '}
              {data.dropOff.location.zipCode}
            </Typography>
          </Box>
        </Box>
        {hasRequiredTimestamps && (
          <PricingContent
            tripLeg={tripLeg}
            pricings={pricings}
            chargeTypes={chargeTypes}
          />
        )}
        <Paper>
          <Box className={classes.reason}>
            <Box className={classes.reasonHeader}>
              <Typography>{t('needsAttention.reason')}</Typography>
              <HelpOutline sx={{ width: 16, height: 16 }} color="primary" />
            </Box>
            <TextField
              id="filled-multiline-static"
              multiline
              variant="filled"
              fullWidth
              rows={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={classes.reasonTextBox}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!reason}
          onClick={submitDiscussion}>
          {t('common.submit')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
